'use client';

import { ReactNode, Suspense, useMemo } from 'react';

import { Banner } from '@flipster/apis/v1/banner/types';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';

import { ContinueWithApple, ContinueWithGoogle } from '../continue-with-button';
import { DownloadAppButton } from '../download-app-button';
import EmailTextFieldCTA from './EmailTextFieldCTA';
import { RollingBanner } from './rolling-banner';

interface Props {
  heroCarousel?: Banner[];
}

const BaseHero = ({ heroCarousel }: Props) => {
  const isMounted = useMounted();
  const { isLoggedIn } = useAuth();

  const rollingBannerItems = useMemo(
    () =>
      heroCarousel?.map((item) =>
        item.desktopVideo
          ? item
          : {
              ...item,
              desktopImage: `${item.desktopImage}?fm=webp&q=80`,
              mobileImage: `${item.mobileImage}?fm=webp&q=80`,
            },
      ) ?? [],
    [heroCarousel],
  );

  return (
    <section
      className={flex({
        w: 'auto',
        maxW: { tablet: 'desktopMaxWidth' },
        mx: { base: 0, tablet: 16, smallDesktop: 'auto' },
        flexDirection: { base: 'column', tablet: 'row' },
        alignItems: 'center',
        justifyContent: 'space-between',
        pt: { base: 24, tablet: 48 },
        pb: { base: 0, tablet: 48 },
        gap: 24,
      })}
    >
      <div className={css({ w: '100%', maxW: { tablet: 520 }, px: { tabletDown: 16 } })}>
        <h1
          className={css({
            textStyle: 'Display/XXL',
            color: 'inverse.white',
            textAlign: { tabletDown: 'center' },
            whiteSpace: 'pre-line',
          })}
        >
          <FormattedMessage
            id='Home.r260702.Global_markets_perpetual_access_multiline'
            values={
              {
                b: (children: ReactNode) => <b className={css({ color: 'content.accent.info' })}>{children}</b>,
              } as never
            }
          />
        </h1>
        <p
          className={css({
            textStyle: 'Title/S',
            color: 'inverse.white',
            mt: { base: 4, tablet: 16 },
            textAlign: { tabletDown: 'center' },
          })}
        >
          <FormattedMessage id='Home.r260702.Seamless_access_to_both_tradFi_and_crypto' />
        </p>
        {isMounted && isLoggedIn ? (
          <div
            className={vstack({
              w: '100%',
              maxW: { tablet: 180 },
              gap: { base: 12, tablet: 20 },
              pt: { base: 24, tablet: 48 },
              alignItems: 'flex-start',
            })}
          >
            <Button
              hierarchy='primary'
              color='accent'
              size='large'
              as='a'
              href='/trade/latest'
              label={<FormattedMessage id='Common.r251104.Trade_now' />}
              onClick={() => segment.track(MainEvent.MainPageTradeNowClick)}
              css={{ minW: { base: '100%', tablet: 120 } }}
            />
            <DownloadAppButton />
          </div>
        ) : (
          <div className={css({ w: '100%', maxW: { tablet: 470 }, pt: { base: 24, tablet: 48 } })}>
            <div className={css({ mb: { base: 12, tablet: 20 } })}>
              <EmailTextFieldCTA />
            </div>
            <div
              className={flex({
                justify: 'space-between',
                flexDirection: { base: 'column', tablet: 'row' },
                gap: 24,
              })}
            >
              <div
                className={flex({
                  display: { tabletDown: 'none', tablet: 'flex' },
                  align: { tablet: 'center' },
                  flexDirection: { base: 'column', tablet: 'row' },
                  textStyle: 'Text/M/Strong',
                  color: 'content.default.level-2',
                  gap: 8,
                })}
              >
                <p>
                  <FormattedMessage id='Main.r251104.Continue_with' />
                </p>
                <div className={hstack({ gap: 8 })}>
                  <Suspense>
                    <ContinueWithGoogle />
                  </Suspense>
                  <Suspense>
                    <ContinueWithApple />
                  </Suspense>
                </div>
              </div>
              <DownloadAppButton />
            </div>
          </div>
        )}
      </div>
      <RollingBanner items={rollingBannerItems} />
    </section>
  );
};

export default BaseHero;
